import { Button, Typography } from "@suraasa/placebo-ui"
import CpatLogo from "features/Cpat/assets/cpat-mini-logo.webp"
import CpatQuestionnaireOnboarding from "features/Cpat/CpatQuestionnaireOnboarding"
import SuccessDialog from "features/Cpat/CpatQuestionnaireOnboarding/components/SuccessDialog"
import {
  cpatQuestionnaireTourManager,
  CpatQuestionnaireTourStatus,
} from "features/Cpat/CpatQuestionnaireOnboarding/utils/tourManager"
import { startCpatAssessment } from "features/Cpat/helpers"
import { useCpat } from "features/Cpat/useCpat"
import { CPAT_TOUR_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/CpatTour/constants"
import { useGlobalContext } from "global/Context/context"
import { useState } from "react"
import routes from "routes"
import {
  BROWSER_STORAGE_KEYS,
  cpatDurationInMinutes,
  cpatPattern,
  cpatQuestions,
} from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"
import { UserPreferenceManager } from "utils/userPreferenceManager"

const CpatStartAssessmentBanner = () => {
  const [startQuestionnaire, setStartQuestionnaire] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const { attempt } = useCpat()

  const {
    cpat: { throughGSG },
  } = useEnrollments()

  const cpatQuestionnaire = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.cpatOnboardingQuestionnaireStatus
  ) as CpatQuestionnaireTourStatus

  const { isNcte } = useGlobalContext()

  const productName = throughGSG
    ? "Comprehensive Pedagogy Assessment of Teachers - Global Schools Group"
    : "Comprehensive Pedagogy Assessment of Teachers"

  const name = isNcte
    ? "National Council for Teacher Education Assessment"
    : productName

  const showCpatQuestionnaire =
    cpatQuestionnaire !== CpatQuestionnaireTourStatus.completed

  const signupOnboardingCompleted =
    UserPreferenceManager.get(
      BROWSER_STORAGE_KEYS.signupOnboardingCompleted
    ) === "true"

  const hasIncompleteData = !signupOnboardingCompleted && showCpatQuestionnaire

  return (
    <>
      <div
        className="mb-8 mt-0.5 rounded-[12px] p-3"
        style={{
          background:
            "linear-gradient(87.05deg, #ECE3EA 0%, #F7F5F9 49.92%, #ECDAF0 99.85%)",
        }}
      >
        <div className="flex flex-row justify-between gap-3">
          <div className="flex flex-col">
            <Typography
              variant="smallBody"
              className="mb-1 text-[12px] font-semibold text-onSurface-500"
            >
              ASSESSMENT
            </Typography>
            <Typography variant="title3" className="mb-1.5">
              {name}
            </Typography>
            <Typography variant="smallBody" className="text-onSurface-500">
              It&apos;s time to evaluate your pedagogy skills! Start your CPAT
              now.
            </Typography>
          </div>
          <img src={CpatLogo} alt="" className="size-4.5" />
        </div>
        <div className="h-3"></div>
        <div className="flex flex-wrap-reverse justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              color="black"
              className={CPAT_TOUR_TARGETS.assessmentAvailable}
              onClick={() => {
                // if (hasIncompleteData) {
                //   setStartQuestionnaire(true)
                //   cpatQuestionnaireTourManager.initiated()
                //   return
                // }

                if (!attempt.data) {
                  throw new Error("No attempt data found")
                }

                trackingService.trackEvent(
                  TrackingEvents.cpatAttemptAssessment,
                  undefined,
                  {
                    services: ["PostHog"],
                  }
                )

                startCpatAssessment({
                  attemptId: attempt.data.id.toString(),
                  isProctored: attempt.data.assessment.isProctored,
                })
              }}
            >
              Start Assessment
            </Button>
            <Button
              variant={"text"}
              color={null}
              className="text-primary-500"
              onClick={() => {
                const instructionsElement = document.getElementById(
                  "general-instructions"
                )
                if (instructionsElement) {
                  instructionsElement.scrollIntoView({
                    behavior: "smooth",
                  })
                }
              }}
            >
              Read Instructions
            </Button>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-[3px]">
              <Typography variant={"smallBody"} className="text-onSurface-500">
                Duration
              </Typography>
              <Typography
                variant={"strongSmallBody"}
                className="text-onSurface-600"
              >
                {cpatDurationInMinutes} mins
              </Typography>
            </div>
            <div className="flex flex-col gap-[3px]">
              <Typography variant={"smallBody"} className="text-onSurface-500">
                Questions
              </Typography>
              <Typography
                variant={"strongSmallBody"}
                className="text-onSurface-600"
              >
                {cpatQuestions}
              </Typography>
            </div>

            <div className="flex flex-col gap-[3px]">
              <Typography variant={"smallBody"} className="text-onSurface-500">
                Pattern
              </Typography>
              <Typography
                variant={"strongSmallBody"}
                className="text-onSurface-600"
              >
                {cpatPattern}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      {startQuestionnaire && (
        <CpatQuestionnaireOnboarding
          open={startQuestionnaire}
          onComplete={() => {
            setTimeout(() => {
              setShowSuccessDialog(true)
            }, 500)
          }}
          onClose={() => {
            setStartQuestionnaire(false)
          }}
        />
      )}
      {showSuccessDialog && (
        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          onProceed={() => {
            setShowSuccessDialog(false)
            if (!attempt.data) {
              throw new Error("No attempt data found")
            }

            trackingService.trackEvent(
              TrackingEvents.cpatOnboardingTourStatus,
              {
                type: "cpat_questionnaire",
                status: "proceed_clicked",
              }
            )

            startCpatAssessment({
              attemptId: attempt.data.id.toString(),
              isProctored: attempt.data.assessment.isProctored,
            })
          }}
        />
      )}
    </>
  )
}

export default CpatStartAssessmentBanner
